<template>
  <div class="categories-filters-tags overflow-auto">
    <!-- Shimmer loading state -->
    <div v-if="isLoadingCategories" class="flex min-w-max gap-2">
      <div v-for="i in 6" :key="`shimmer-${i}`" class="shimmer-tag">
        <div class="shimmer-text"></div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="categoriesError" class="error-state">
      <span>{{ categoriesError }}</span>
    </div>

    <!-- Categories tags -->
    <div v-else class="flex min-w-max gap-2">
      <!-- Afficher un tag pour chaque catégorie disponible -->
      <div
        v-for="category in availableCategories"
        :key="category.id"
        :class="[
          'category-tag',
          'px-3',
          'py-1',
          'rounded-full',
          'text-sm',
          'font-medium',
          'cursor-pointer',
          'transition-all',
          'duration-200',
          'ease-in-out',
          'flex',
          'items-center',
          'gap-1',
          'hover:scale-105',
          category.isSelected
            ? `${category.color || 'bg-primary'} text-white shadow-md`
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        ]"
        @click="toggleCategory(category)"
      >
        <!-- Optionnel: Afficher l'icône si disponible -->
        <span v-if="category.icon" :class="['w-4', 'h-4', 'flex-shrink-0']">
          <ImgComponent
            :src="category.icon"
            :alt="category.name"
            class="w-full h-full object-contain"
          />
        </span>
        <span>{{ category.name }}</span>
        <!-- Indicateur visuel si sélectionné -->
        <span v-if="category.isSelected" class="ml-1 text-xs"> ✓ </span>
      </div>
    </div>

    <!-- Afficher un indicateur visuel quand des filtres actifs -->
    <!-- <div
      v-if="hasActiveFilters"
      class="mt-3 text-sm text-gray-600 inline-flex items-center gap-1 sticky bottm-0 left-0"
    >
      <span class="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
      <span>Catégories sélectionnées: {{ selectedCategoriesCount }}</span>
    </div> -->
  </div>
</template>

<script setup>
import { computed, ref, getCurrentInstance, onMounted, onUnmounted } from 'vue';

const { proxy } = getCurrentInstance();
const { $stores, $constants, $bus } = proxy;

// Local state for categories loaded from server
const isLoadingCategories = ref(false);
const categoriesError = ref(null);
const categories = ref([]);

// Load categories from server API using network manager
const loadCategories = () => {
  console.log('[CategoriesFiltersTags] Loading categories from server');

  // Show shimmer placeholders while loading
  isLoadingCategories.value = true;
  categoriesError.value = null;

  // Emit network request
  $bus?.emit($constants?.EVENTS?.NETWORK?.REQUEST, {
    id: 'load-categories-tags',
    url: '/categories/list?group=ultraxeno',
    method: 'GET'
  });
};

// Handle network response for categories
const handleCategoriesResponse = (res) => {
  console.log('[CategoriesFiltersTags] Network response received:', res);

  if (!res) {
    console.warn('[CategoriesFiltersTags] Received empty response');
    return;
  }

  if (res.id === 'load-categories-tags') {
    isLoadingCategories.value = false;

    if (res.success) {
      // Store fetched categories
      const fetchedCategories = res.data?.data || res.data || [];
      console.log(
        '[CategoriesFiltersTags] Categories loaded:',
        fetchedCategories.length
      );
      categories.value = fetchedCategories;
    } else {
      const errorMessage =
        res.data?.message || 'Erreur lors du chargement des catégories';
      categoriesError.value = errorMessage;
      console.error(
        '[CategoriesFiltersTags] Failed to load categories:',
        errorMessage
      );
    }
  }
};

// Données réactives
const availableCategories = computed(() => {
  // Use local categories loaded from server instead of home store
  const serverCategories = categories.value;

  // Get selected categories from searchFilters store (array of category objects)
  const selectedCategories =
    $stores.searchFilters?.state?.selectedCategories || [];

  // Mapper les catégories avec un état de sélection
  return serverCategories.map((category) => ({
    ...category,
    isSelected:
      selectedCategories.some((cat) => cat.id === category.id) || false
  }));
});

const hasActiveFilters = computed(() => {
  return $stores.searchFilters?.hasActiveFilters || false;
});

const selectedCategoriesCount = computed(() => {
  return $stores.searchFilters?.state?.selectedCategories?.length || 0;
});

// Méthodes
const toggleCategory = (category) => {
  console.log('[CategoriesFiltersTags] Toggling category:', category);

  if (!category || !category.id) {
    console.error('[CategoriesFiltersTags] Invalid category provided');
    return;
  }

  // Emit CATEGORY_TOGGLED event with category object to update searchFilters store
  $bus?.emit($constants.EVENTS.SEARCH_FILTERS.CATEGORY_TOGGLED, category);
};

// Lifecycle hooks
onMounted(() => {
  console.log('[CategoriesFiltersTags] Component mounted');

  if (!$bus || !$constants) {
    console.error('[CategoriesFiltersTags] $bus or $constants not available');
    return;
  }

  // Listen to network responses for categories
  $bus.on($constants.EVENTS.NETWORK.RESPONSE, handleCategoriesResponse);

  // Load categories from server
  loadCategories();

  // Listen to filters updated to refresh UI
  // Note: We don't need to store this reference as we use an arrow function
  $bus.on($constants.EVENTS.SEARCH_FILTERS.FILTERS_UPDATED, (filters) => {
    console.log('[CategoriesFiltersTags] Filters updated:', filters);
    // The computed property will automatically update the UI
  });

  console.log('[CategoriesFiltersTags] Event listeners registered');
});

onUnmounted(() => {
  console.log('[CategoriesFiltersTags] Component unmounting');

  // Clean up event listeners
  if (!$bus || !$constants) {
    console.warn(
      '[CategoriesFiltersTags] $bus or $constants not available during cleanup'
    );
    return;
  }

  $bus.off($constants.EVENTS.NETWORK.RESPONSE, handleCategoriesResponse);
  // Note: Cannot remove the arrow function listener for FILTERS_UPDATED without storing a reference
  // This is a known limitation, but the memory impact is minimal
  $bus.off($constants.EVENTS.SEARCH_FILTERS.FILTERS_UPDATED);

  console.log('[CategoriesFiltersTags] Event listeners cleaned up');
});
</script>

<style scoped>
.categories-filters-tags {
  width: 100%;
}

/* Shimmer Loading State */
.shimmer-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  background: #f0f0f0;
  min-width: 80px;
  height: 32px;
}

.shimmer-text {
  width: 60px;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Error State */
.error-state {
  padding: 12px;
  background: #fee;
  color: #c33;
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
}

.category-tag {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Animation pour les changements d'état */
.category-tag {
  position: relative;
  overflow: hidden;
}

.category-tag::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition:
    width 0.6s,
    height 0.6s;
}

.category-tag:active::after {
  width: 100px;
  height: 100px;
}
</style>
